import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Typography, ButtonBase } from "@mui/material";
import ArrowCircleUpOutlinedIcon from "@mui/icons-material/ArrowCircleUpOutlined";
import dayjs from "dayjs";
import UnderPaymentListSidePanel from "./UnderPaymentListSidePanel";
import UnderPaymentListTable from "./UnderPaymentListTable";
import { roundToTwoDecimals } from "../../../../utils/formHelper";
import { getApi } from "../../../../utils/services";
import { GET_PREVAILING_WAGE_UNDER_PAYMENT_OVERVIEW, GET_PREVAILING_WAGE_UNDER_PAYMENT_EXPORT } from "../../../../utils/services/apiPath";
import { createODataQuery } from "../../../../utils/services/oData";
import { convertToCSV, downloadCSV } from "../../../../utils/csvExporter";
import { errorToast } from "../../../../utils/toastHelper";
import "./UnderPaymentList.scss";

const GROUP_BY_OPTIONS = [
  { key: "payPeriod", label: "Pay period" },
  { key: "project", label: "Project" }
];

const SORT_FIELD_BY_GROUP = {
  payPeriod: "payPeriodEndingDate",
  project: "recordCount"
};

const DEFAULT_SORT_DIRECTION = {
  payPeriod: "asc",
  project: "desc"
};

const EMPTY_SUMMARY = { totalRecords: 0, totalBackPayment: 0, projectCount: 0, payPeriodCount: 0 };

const formatCurrency = value =>
  `$${Number(roundToTwoDecimals(Number(value ?? 0))).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const mapOverviewGroup = groupBy => group => {
  if (groupBy === "payPeriod") {
    return {
      key: group.payPeriodEndingDate,
      label: group.payPeriodEndingDate ? `Week ending ${dayjs(group.payPeriodEndingDate).format("MM/DD/YYYY")}` : "Week ending —",
      count: group.recordCount ?? 0,
      amount: group.backPaymentAmount ?? 0,
      payPeriodEndingDate: group.payPeriodEndingDate
    };
  }

  return {
    key: group.projectGuid,
    label: group.projectName ?? "Unknown project",
    count: group.recordCount ?? 0,
    amount: group.backPaymentAmount ?? 0,
    projectGuid: group.projectGuid
  };
};

const buildProjectFilter = projectIds =>
  projectIds ? `projectGuid in (${projectIds})` : "";

const buildOverviewUrl = (groupBy, sortDirection, projectFilter) => {
  const orderCol = SORT_FIELD_BY_GROUP[groupBy];
  const odataQuery = createODataQuery(
    [],
    [],
    projectFilter,
    "",
    { orderCol, orderByAscending: sortDirection === "asc" },
    0,
    0,
    false
  );
  const queryParams = odataQuery
    .replace("?", "")
    .split("&")
    .filter(param => param && !param.startsWith("$count"))
    .join("&");

  return `${GET_PREVAILING_WAGE_UNDER_PAYMENT_OVERVIEW}?groupBy=${groupBy}${queryParams ? `&${queryParams}` : ""}`;
};

export default function UnderPaymentList({ onSelectRecord, onClose, projectIds }) {
  const projectFilter = useMemo(() => buildProjectFilter(projectIds), [projectIds]);

  const [groupBy, setGroupBy] = useState("payPeriod");
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION.payPeriod);
  const [selectedGroupKey, setSelectedGroupKey] = useState(null);

  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [groups, setGroups] = useState([]);
  const [isGroupsLoading, setIsGroupsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsGroupsLoading(true);
      try {
        const response = await getApi(buildOverviewUrl(groupBy, sortDirection, projectFilter));
        if (cancelled) return;

        const apiError = response?.error;
        const apiData = response && typeof response === "object" && "data" in response ? response.data : response;

        if (apiError) {
          errorToast(apiError?.message || apiError || "Unable to load prevailing wage underpayments.");
          return;
        }

        setSummary({
          totalRecords: apiData?.summary?.recordCount ?? 0,
          totalBackPayment: apiData?.summary?.backPaymentAmount ?? 0,
          projectCount: apiData?.summary?.projectCount ?? 0,
          payPeriodCount: apiData?.summary?.payPeriodCount ?? 0
        });
        setGroups((apiData?.groups ?? []).map(mapOverviewGroup(groupBy)));
      } catch (error) {
        if (!cancelled) errorToast(error?.message || "Unable to load prevailing wage underpayments.");
      } finally {
        if (!cancelled) setIsGroupsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [groupBy, sortDirection, projectFilter]);

  const activeGroup = useMemo(() => {
    if (!groups.length) return null;
    return groups.find(group => group.key === selectedGroupKey) ?? groups[0];
  }, [groups, selectedGroupKey]);

  const handleGroupByChange = nextGroupBy => {
    if (nextGroupBy === groupBy) return;
    setIsGroupsLoading(true);
    setGroups([]);
    setSelectedGroupKey(null);
    setSortDirection(DEFAULT_SORT_DIRECTION[nextGroupBy]);
    setGroupBy(nextGroupBy);
  };

  const handleGroupSelect = key => {
    setSelectedGroupKey(key);
  };

  const handleToggleSort = () => {
    setSortDirection(current => (current === "asc" ? "desc" : "asc"));
  };

  const handleExportCsv = async () => {
    if (isExporting || !activeGroup || activeGroup.count === 0) return;

    setIsExporting(true);
    try {
      const groupExpression =
        groupBy === "payPeriod"
          ? `payPeriodEndingDate eq ${activeGroup.payPeriodEndingDate}`
          : `projectGuid eq ${activeGroup.projectGuid}`;
      const filterExpression = [groupExpression, projectFilter].filter(Boolean).join(" and ");

      // No $top: export rows are per work area, so there can be more of them than the group's record count
      const odataQuery = createODataQuery(
        [],
        [],
        filterExpression,
        "",
        { orderCol: "payPeriodEndingDate", orderByAscending: true },
        0,
        0,
        false
      );
      const response = await getApi(`${GET_PREVAILING_WAGE_UNDER_PAYMENT_EXPORT}${odataQuery}`);

      const apiError = response?.error;
      if (apiError) {
        errorToast(apiError?.message || apiError || "Unable to export prevailing wage underpayments.");
        return;
      }

      const apiData = response && typeof response === "object" && "data" in response ? response.data : response;
      const records = Array.isArray(apiData) ? apiData : apiData?.value ?? [];
      if (!records.length) {
        errorToast("No data to export.");
        return;
      }

      const csvRows = records.map(record => ({
        "Employee": record.employeeName,
        "Project": record.projectName,
        "Pay Period": record.payPeriodEndingDate ? dayjs(record.payPeriodEndingDate).format("MM/DD/YYYY") : "",
        "Labor Classification": record.laborClassification,
        "Prevailing Wage ST": record.prevailingWageStraightTime,
        "Prevailing Wage DT": record.prevailingWageDoubleTime,
        "Prevailing Wage OT": record.prevailingWageOverTime,
        "Prevailing Fringe ST": record.prevailingFringeStraightTime,
        "Prevailing Fringe DT": record.prevailingFringeDoubleTime,
        "Prevailing Fringe OT": record.prevailingFringeOverTime,
        "Work Area Name": record.workAreaName ?? "",
        "ST Hours": record.straightTimeHours,
        "OT Hours": record.overTimeHours,
        "DT Hours": record.doubleTimeHours,
        "Wage Required": record.wageRequired,
        "Gross Amount Earned (7A)": record.grossAmountEarned,
        "Wage Back Payment": record.wageBackPayment,
        "Fringe Required": record.fringeRequired,
        "Total Fringe Benefit Credit (6B)": record.totalFringeBenefitCredit,
        "Payment in Lieu of Fringe Benefits (6C)": record.paymentInLieuOfFringe,
        "Fringe Back Payment": record.fringeBackPayment,
        "Back Payment (Interest Not Included)": record.backPaymentAmount
      }));

      const fileSuffix =
        groupBy === "payPeriod"
          ? dayjs(activeGroup.payPeriodEndingDate).format("MM-DD-YYYY")
          : (activeGroup.label ?? "export").replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "");
      downloadCSV(convertToCSV(csvRows), `PrevailingWageUnderpayments-${fileSuffix}.csv`);
    } catch (error) {
      errorToast(error?.message || "Unable to export prevailing wage underpayments.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="underpayment-list">
      <div className="underpayment-list-header">
        <div className="underpayment-list-heading">
          <Typography className="underpayment-list-title">Prevailing Wage Underpayments</Typography>
          <Typography className="underpayment-list-subtitle">
            {summary.totalRecords.toLocaleString("en-US")} records &middot; {formatCurrency(summary.totalBackPayment)} back
            payment &middot; {summary.projectCount} project{summary.projectCount === 1 ? "" : "s"} &middot;{" "}
            {summary.payPeriodCount} pay period{summary.payPeriodCount === 1 ? "" : "s"}
          </Typography>
        </div>

        <div className="underpayment-list-actions">
          <span className="underpayment-list-groupby-label">Group by</span>
          <div className="underpayment-list-groupby-toggle">
            {GROUP_BY_OPTIONS.map(option => (
              <ButtonBase
                key={option.key}
                className={`underpayment-list-groupby-btn ${
                  groupBy === option.key ? "underpayment-list-groupby-btn--active" : ""
                }`}
                onClick={() => handleGroupByChange(option.key)}
              >
                {option.label}
              </ButtonBase>
            ))}
          </div>

          <ButtonBase
            className="underpayment-list-export-btn"
            onClick={handleExportCsv}
            disabled={isGroupsLoading || isExporting || !activeGroup || activeGroup.count === 0}
          >
            <ArrowCircleUpOutlinedIcon className="underpayment-list-export-btn-icon" />
            Export CSV
          </ButtonBase>
        </div>
      </div>

      <div className="underpayment-list-divider" />

      <div className="underpayment-list-body">
        <UnderPaymentListSidePanel
          columnLabel={groupBy === "payPeriod" ? "Pay period" : "Project"}
          groups={groups}
          selectedKey={activeGroup?.key}
          onSelect={handleGroupSelect}
          formatCurrency={formatCurrency}
          sortDirection={sortDirection}
          onToggleSort={handleToggleSort}
          isLoading={isGroupsLoading}
        />

        <UnderPaymentListTable
          key={`${groupBy}-${activeGroup?.key ?? "empty"}`}
          groupBy={groupBy}
          activeGroup={activeGroup}
          onSelectRecord={onSelectRecord}
          formatCurrency={formatCurrency}
          isGroupsLoading={isGroupsLoading}
          projectFilter={projectFilter}
        />
      </div>
    </div>
  );
}

UnderPaymentList.propTypes = {
  onSelectRecord: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  projectIds: PropTypes.string
};

UnderPaymentList.defaultProps = {
  onClose: null,
  projectIds: ""
};